/**
 * DRPX Courier — FahrerDataContext
 *
 * Zentraler Data-Provider für alle Fahrer-Datentypen.
 * Lädt Daten EINMAL und teilt sie über Context mit allen Fahrer-Pages.
 *
 * CRUD-Logik liegt in useFahrerData.ts (mit apiSilent).
 * Dieser Context stellt nur Load + Shared State bereit.
 *
 * @module contexts/FahrerDataContext
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../lib/supabase.js';
import type { Dispatch, SetStateAction } from 'react';
import type {
  FahrerKunde,
  FahrerAuftrag,
  FahrerRechnung,
  FahrerBuchung,
  Fahrt,
} from '../types.js';

// ═══════════════════════════════════════════════════════════════
// Exported Context-Value Interface
// ═══════════════════════════════════════════════════════════════

/** Einzelnes Daten-Segment im FahrerDataContext. */
interface DataSlice<T> {
  data: T[];
  setData: Dispatch<SetStateAction<T[]>>;
  loaded: boolean;
  reload: () => void;
}

/** Shape des gesamten FahrerDataContext. */
export interface FahrerDataCtxValue {
  kunden: {
    kunden: FahrerKunde[];
    setKunden: Dispatch<SetStateAction<FahrerKunde[]>>;
    reloadKunden: () => void;
  };
  auftraege: {
    auftraege: FahrerAuftrag[];
    setAuftraege: Dispatch<SetStateAction<FahrerAuftrag[]>>;
    reloadAuftraege: () => void;
  };
  rechnungen: {
    rechnungen: FahrerRechnung[];
    setRechnungen: Dispatch<SetStateAction<FahrerRechnung[]>>;
    reloadRechnungen: () => void;
  };
  fahrten: {
    fahrten: Fahrt[];
    setFahrten: Dispatch<SetStateAction<Fahrt[]>>;
    reloadFahrten: () => void;
  };
  buchungen: {
    buchungen: FahrerBuchung[];
    setBuchungen: Dispatch<SetStateAction<FahrerBuchung[]>>;
    loaded: boolean;
    reloadBuchungen: () => void;
  };
}

// ═══════════════════════════════════════════════════════════════
// Context + Convenience-Hook
// ═══════════════════════════════════════════════════════════════

export const FahrerDataContext = createContext<FahrerDataCtxValue | null>(null);

/**
 * useFahrerDataCtx — Convenience-Hook zum Lesen des Contexts.
 * Gibt null zurück wenn kein Provider vorhanden (Fallback-Modus).
 */
export function useFahrerDataCtx(): FahrerDataCtxValue | null {
  return useContext(FahrerDataContext);
}

// ═══════════════════════════════════════════════════════════════
// Generischer Load-Hook (DRY für alle 5 Typen)
// ═══════════════════════════════════════════════════════════════

/** Cancellation-Token für Race-Condition-Schutz. */
interface CancelToken {
  cancelled: boolean;
}

/** Optionen für useLoadHook. */
interface LoadHookOpts {
  dedupKey?: string;
}

/** Load-Funktion: nimmt userId, gibt { data: T[] } zurück. */
type LoadFn<T> = (userId: string) => Promise<{ data: T[]; error?: unknown }>;

function useLoadHook<T>(
  userId: string,
  loadFn: LoadFn<T>,
  opts?: LoadHookOpts,
): DataSlice<T> {
  const dedupKey = opts?.dedupKey;
  const [data, setData] = useState<T[]>([]);
  const [loaded, setLoaded] = useState(false);
  const cancelRef = useRef<CancelToken | null>(null);

  const load = useCallback(
    () => {
      if (!userId) return;
      // Alte in-flight Request invalidieren (Race-Condition-Schutz)
      if (cancelRef.current) cancelRef.current.cancelled = true;
      const token: CancelToken = { cancelled: false };
      cancelRef.current = token;
      loadFn(userId)
        .then((res) => {
          if (token.cancelled) return;
          if (res.data) {
            let result = res.data;
            if (dedupKey) {
              const seen = new Set<unknown>();
              result = result.filter((item) => {
                const key = (item as Record<string, unknown>)[dedupKey];
                if (!key) return true;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            }
            setData(result);
          }
          setLoaded(true);
        })
        .catch(() => { /* DB-Layer hat Error bereits propagiert */ });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  );

  useEffect(
    () => {
      load();
      return () => {
        if (cancelRef.current) cancelRef.current.cancelled = true;
      };
    },
    [load],
  );

  return { data, setData, loaded, reload: load };
}

// ═══════════════════════════════════════════════════════════════
// FahrerDataProvider — Wraps Fahrer-Pages, lädt alle Daten
// einmal und stellt sie über Context bereit.
// ═══════════════════════════════════════════════════════════════

interface FahrerDataProviderProps {
  userId: string;
  children: React.ReactNode;
}

export function FahrerDataProvider({ userId, children }: FahrerDataProviderProps): React.JSX.Element {
  const kundenHook = useLoadHook<FahrerKunde>(userId, db.getFahrerKunden);
  const auftraegeHook = useLoadHook<FahrerAuftrag>(userId, db.getFahrerAuftraege);
  const rechnungenHook = useLoadHook<FahrerRechnung>(userId, db.getFahrerRechnungen);
  const fahrtenHook = useLoadHook<Fahrt>(userId, db.getFahrten);
  const buchungenHook = useLoadHook<FahrerBuchung>(userId, db.getFahrerBuchungen, { dedupKey: 'auftragId' });

  const value: FahrerDataCtxValue = {
    kunden: {
      kunden: kundenHook.data,
      setKunden: kundenHook.setData,
      reloadKunden: kundenHook.reload,
    },
    auftraege: {
      auftraege: auftraegeHook.data,
      setAuftraege: auftraegeHook.setData,
      reloadAuftraege: auftraegeHook.reload,
    },
    rechnungen: {
      rechnungen: rechnungenHook.data,
      setRechnungen: rechnungenHook.setData,
      reloadRechnungen: rechnungenHook.reload,
    },
    fahrten: {
      fahrten: fahrtenHook.data,
      setFahrten: fahrtenHook.setData,
      reloadFahrten: fahrtenHook.reload,
    },
    buchungen: {
      buchungen: buchungenHook.data,
      setBuchungen: buchungenHook.setData,
      loaded: buchungenHook.loaded,
      reloadBuchungen: buchungenHook.reload,
    },
  };

  return (
    <FahrerDataContext.Provider value={value}>
      {children}
    </FahrerDataContext.Provider>
  );
}

export default FahrerDataProvider;
